import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Text,
  useBreakpointValue,
  Stack,
  Flex,
  Tooltip,
  Input,
  Button,
  Collapse,
} from "@chakra-ui/react";
import { fetchTopDonations } from "../api/api"; // kept import in case other code expects it
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

/**
 * TopDonations (updated)
 * - Fetches /api/top/donations?num=20
 * - Shows dropdown toggle for Android/iOS (mobile platform),
 *   desktop/laptop/iPad shows full view (no dropdown).
 * - Increased rank coloring to 20 entries.
 * - Removed automatic reverse auto-scroll on desktop/tablet/iPad.
 */

// ✅ FittingName — stricter flex rules + smaller base font for tighter truncation on mobile
const FittingName = ({ children }) => {
  return (
    <Tooltip
      label={children}
      hasArrow
      placement="top"
      bg="gray.700"
      color="white"
    >
      <Text
        as="span"
        // ensure text truncates correctly inside flex containers
        display="block"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        // more aggressive responsive sizing for very long names
        fontSize={{ base: "11px", sm: "12px", md: "clamp(12px, 2.2vw, 16px)" }}
        fontWeight="bold"
        letterSpacing={{ base: "-0.03em", md: "normal" }}
        lineHeight="1"
        // allow the Text to shrink inside flex containers
        flex="1 1 auto"
        minW="0"
        maxW="100%"
      >
        {children}
      </Text>
    </Tooltip>
  );
};

// Rank-based background colors — expanded to 20 entries
const rankColors = [
  "#FFD70055", // 1st - Gold
  "#C0C0C055", // 2nd - Silver
  "#CD7F3255", // 3rd - Bronze
  "#90EE9055", // 4th - Light Green
  "#87CEFA55", // 5th - Light Blue
  "#FFB6C155", // 6th - Light Pink
  "#FFA07A55", // 7th - Light Salmon
  "#DDA0DD55", // 8th - Plum
  "#20B2AA55", // 9th - Light Sea Green
  "#FFE4B555", // 10th - Moccasin
  "#F0E68C55", // 11th - Khaki
  "#E9967A55", // 12th - Dark Salmon
  "#AFEEEE55", // 13th - Pale Turquoise
  "#F5DEB355", // 14th - Wheat
  "#B0C4DE55", // 15th - Light Steel Blue
  "#87CEFA55", // 16th - PapayaWhip
  "#be165155", // 17th - BlanchedAlmond
  "#FFB6C155", // 18th - LightCyan
  "#7c73bc55", // 19th - Beige
  "#90EE9055", // 20th - Seashell
];

const TopDonations = () => {
  const [topDonors, setTopDonors] = useState([]);
  const [filteredDonors, setFilteredDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const fontSize = useBreakpointValue({ base: "sm", md: "md" });
  const isMobileBreakpoint = useBreakpointValue({ base: true, md: false }); // original breakpoint usage

  // Detect Android / iOS user agent for the mobile dropdown behavior
  const [isMobilePlatform, setIsMobilePlatform] = useState(false);
  useEffect(() => {
    try {
      const ua =
        typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
      setIsMobilePlatform(/Android|iPhone|iPad|iPod/i.test(ua));
    } catch {
      setIsMobilePlatform(false);
    }
  }, []);

  const scrollRef = useRef(null);

  // Mobile dropdown state: collapsed by default on mobile platforms.
  // Desktop / laptop / iPad will ignore the collapsed state (always show full list).
  const [mobileExpanded, setMobileExpanded] = useState(false);

  // fetch top 20 donors — use direct fetch to ensure '?num=20' is used.
  useEffect(() => {
    let mounted = true;
    setLoading(true);

    // prefer relative path; change if you require absolute host
    fetch("/api/top/donations?num=20")
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        // backend might return array or { data: [...] }
        const data = Array.isArray(json) ? json : json?.data ?? [];
        if (mounted) {
          const top20 = data.slice(0, 20);
          setTopDonors(top20);
          setFilteredDonors(top20);
        }
      })
      .catch((err) => {
        // Fall back to existing helper if present (keeps compatibility)
        if (typeof fetchTopDonations === "function") {
          fetchTopDonations()
            .then((res) => {
              const d = res?.data ?? [];
              const top20 = d.slice(0, 20);
              if (mounted) {
                setTopDonors(top20);
                setFilteredDonors(top20);
              }
            })
            .catch(() => {
              console.error("Error fetching top donors:", err);
            })
            .finally(() => mounted && setLoading(false));
        } else {
          console.error("Error fetching top donors:", err);
        }
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  // 🔍 Search filter (applies to both mobile & desktop)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDonors(topDonors);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredDonors(
        topDonors.filter((donor) =>
          `${donor.donorLastName || ""} ${donor.donorFirstName || ""}`
            .toLowerCase()
            .includes(q)
        )
      );
    }
  }, [searchQuery, topDonors]);

  if (loading) return <Spinner />;
  if (topDonors.length === 0) return <Text>No top donations yet.</Text>;

  // For mobile platforms, show a compact list unless mobileExpanded == true.
  const compactCount = 5;
  const effectiveDonors =
    isMobilePlatform && !mobileExpanded
      ? filteredDonors.slice(0, compactCount)
      : filteredDonors.slice(0, 20);

  return (
    <Box>
      <Text fontSize="xl" fontWeight="bold" mb={2}>
        Top 20 Donors
      </Text>

      {isMobileBreakpoint ? (
        // ---- Mobile View (Android/iOS show dropdown toggle) ----
        <Stack spacing={3}>
          {/* If platform is Android/iOS show toggle button; otherwise behave like desktop mobile-breakpoint but no toggle */}
          {isMobilePlatform && (
            <Button
              onClick={() => setMobileExpanded((s) => !s)}
              alignSelf="flex-start"
              size="sm"
              leftIcon={
                mobileExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />
              }
            >
              {mobileExpanded ? "Hide" : "Show"}
            </Button>
          )}

          <Collapse
            in={!isMobilePlatform ? true : mobileExpanded}
            animateOpacity
          >
            <Stack spacing={3}>
              {effectiveDonors.map((donor, index) => {
                const donorName = `${donor.donorLastName || ""} ${
                  donor.donorFirstName || ""
                }`.trim();
                return (
                  <Box
                    key={donor.id ?? `${index}`}
                    p={{ base: 2, md: 3 }}
                    border="1px solid #eee"
                    borderRadius="md"
                    bg="white"
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      gap={2}
                      minWidth={0}
                    >
                      {/* Name pill */}
                      <Box
                        px={{ base: 1, md: 2 }}
                        py={1}
                        borderRadius="md"
                        bg={rankColors[index] || "gray.100"}
                        flex="1 1 auto"
                        minWidth={0}
                        maxWidth="100%"
                        overflow="hidden"
                        display="flex"
                        alignItems="center"
                      >
                        <FittingName>{donorName}</FittingName>
                      </Box>

                      {/* Amount */}
                      <Text
                        color="orange.700"
                        fontWeight="semibold"
                        fontSize={{ base: "12px", md: "sm" }}
                        flexShrink={0}
                        ml={2}
                        whiteSpace="nowrap"
                      >
                        ₹ {donor.amount?.toLocaleString("en-IN")}
                      </Text>
                    </Flex>
                  </Box>
                );
              })}
            </Stack>
          </Collapse>
        </Stack>
      ) : (
        // ---- Desktop/Tablet/iPad View (no auto-scrolling, full list visible) ----
        <Box
          border="1px solid #eee"
          borderRadius="md"
          bg="white"
          position="relative"
          overflow="hidden"
        >
          {/* Gradient overlays */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            height="40px"
            bg="linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0))"
            pointerEvents="none"
            zIndex="2"
          />
          <Box
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            height="40px"
            bg="linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0))"
            pointerEvents="none"
            zIndex="2"
          />

          <Box p={3}>
            <Input
              placeholder="Search by donor name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="gray.50"
              fontSize={fontSize}
            />
          </Box>

          <TableContainer
            ref={scrollRef}
            maxH="220px"
            overflowY="auto"
            borderTop="1px solid #eee"
            borderRadius="md"
            sx={{
              WebkitOverflowScrolling: "touch",
              "::-webkit-scrollbar": { width: "4px", height: "4px" },
              "::-webkit-scrollbar-thumb": {
                background: "#ccc",
                borderRadius: "4px",
              },
            }}
          >
            <Table variant="striped" size="sm" colorScheme="orange">
              <Thead bg="orange.500" position="sticky" top="0" zIndex={1}>
                <Tr>
                  <Th color="white" fontSize={fontSize}>
                    Donor
                  </Th>
                  <Th color="white" fontSize={fontSize} isNumeric>
                    Amount (₹)
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredDonors.slice(0, 20).map((donor, index) => {
                  const donorName = `${donor.donorLastName || ""} ${
                    donor.donorFirstName || ""
                  }`.trim();
                  return (
                    <Tr
                      key={`${donor.id ?? index}-${index}`}
                      bg={rankColors[index % 20] || "transparent"}
                      transition="all 0.3s ease"
                      _hover={{ transform: "scale(1.01)", bg: "orange.100" }}
                    >
                      <Td fontSize={fontSize} maxW="350px">
                        <Text fontSize={fontSize} noOfLines={1}>
                          {donorName}
                        </Text>
                      </Td>
                      <Td isNumeric fontSize={fontSize}>
                        ₹ {donor.amount?.toLocaleString("en-IN")}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};
export default TopDonations;
